import React, { useMemo } from 'react';
import './PredictionDistributionBar.css';

/**
 * Parse a distribution_json blob into a typed object. Handles both already-
 * parsed objects (passed from memory) and stringified JSON (passed straight
 * from the websocket payload or REST response).
 *
 * Returns null on malformed input rather than throwing — the bar renders
 * empty in that case and the caller's layout stays intact.
 */
export function parseDistribution(input) {
  if (input == null) return null;
  let obj = input;
  if (typeof input === 'string') {
    try { obj = JSON.parse(input); } catch { return null; }
  }
  if (!obj || !Array.isArray(obj.probabilities) || obj.probabilities.length === 0) return null;
  const binMinutes = Number(obj.binMinutes) || 5;
  const startOffsetMin = Number(obj.startOffsetMin) || 0;
  const probabilities = obj.probabilities.map(p => {
    const n = Number(p);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  });
  return {
    binMinutes,
    startOffsetMin,
    probabilities,
    p10Minutes: Number.isFinite(obj.p10Minutes) ? obj.p10Minutes : null,
    p50Minutes: Number.isFinite(obj.p50Minutes) ? obj.p50Minutes : null,
    p90Minutes: Number.isFinite(obj.p90Minutes) ? obj.p90Minutes : null,
  };
}

/**
 * P(arrival > thresholdMinutes) from a binned distribution. Linear-interpolates
 * within the bin containing the threshold. Returns 0 when the threshold is
 * past every bin, 1 when before every bin. Matches PredictionDistribution.cdf
 * in the backend.
 */
export function probAfter(dist, thresholdMinutes) {
  if (!dist || !Number.isFinite(thresholdMinutes)) return 0;
  const { binMinutes, startOffsetMin, probabilities } = dist;
  if (thresholdMinutes <= startOffsetMin) return 1;
  const x = thresholdMinutes - startOffsetMin;
  const fullBins = Math.floor(x / binMinutes);
  if (fullBins >= probabilities.length) return 0;
  let cumulative = 0;
  for (let i = 0; i < fullBins; i++) cumulative += probabilities[i];
  const frac = (x - fullBins * binMinutes) / binMinutes;
  cumulative += probabilities[fullBins] * frac;
  let total = 0;
  for (const p of probabilities) total += p;
  return Math.max(0, Math.min(1, total - cumulative));
}

/**
 * Horizontal stacked bar of a prediction distribution over 5-minute bins.
 * Each bin is opacity-scaled by probability relative to the peak bin — makes
 * mode and shape immediately visible without needing a y-axis.
 *
 * Shows p10/p50/p90 markers as thin vertical lines when present. A hover
 * tooltip (via browser `title=`) gives the bin start offset and probability.
 *
 * Props:
 *   distribution     PredictionDistribution object (parsed) or null
 *   predictionAt     ISO string or Date — base time for converting bin
 *                    offsets to wall-clock HH:MM in tooltips. Optional.
 *   airportTz        IANA timezone name (e.g. "Europe/Dublin") for HH:MM
 *                    formatting. Optional; UTC is the fallback.
 *   width            CSS width. Defaults to 100% of parent.
 *   height           px. Defaults 14.
 *
 * Returns null when the distribution is missing — caller's layout stays
 * stable and shows nothing for v11-only flights.
 */
export default function PredictionDistributionBar({
  distribution,
  predictionAt,
  airportTz,
  width,
  height = 14,
}) {
  const parsed = useMemo(() => parseDistribution(distribution), [distribution]);

  const bins = useMemo(() => {
    if (!parsed) return null;
    const peak = Math.max(1e-9, ...parsed.probabilities);
    return parsed.probabilities.map((p, i) => ({
      idx: i,
      prob: p,
      // Scale opacity by sqrt so small probs are still visible. Peak bin
      // reaches opacity 1; cells with prob==0 render transparent.
      opacity: p > 0 ? Math.min(1, 0.25 + 0.75 * Math.sqrt(p / peak)) : 0,
      startOffsetMin: parsed.startOffsetMin + i * parsed.binMinutes,
      endOffsetMin: parsed.startOffsetMin + (i + 1) * parsed.binMinutes,
    }));
  }, [parsed]);

  if (!parsed || !bins) return null;

  const baseTime = predictionAt
    ? (predictionAt instanceof Date ? predictionAt : new Date(predictionAt.endsWith?.('Z') ? predictionAt : predictionAt + 'Z'))
    : null;

  const formatHM = (minsFromBase) => {
    if (!baseTime) return `+${minsFromBase}m`;
    const d = new Date(baseTime.getTime() + minsFromBase * 60000);
    try {
      return new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit', minute: '2-digit', hour12: false,
        timeZone: airportTz || 'UTC',
      }).format(d);
    } catch {
      return `+${minsFromBase}m`;
    }
  };

  // Position percent for p10/p50/p90 markers. totalSpan is the bar's full
  // minute-range (bins × binWidth). Marker is only drawn if it falls inside.
  const totalSpan = parsed.probabilities.length * parsed.binMinutes;
  const markerPct = (mins) => {
    if (!Number.isFinite(mins)) return null;
    const rel = mins - parsed.startOffsetMin;
    if (rel < 0 || rel > totalSpan) return null;
    return (rel / totalSpan) * 100;
  };

  const p10Pct = markerPct(parsed.p10Minutes);
  const p50Pct = markerPct(parsed.p50Minutes);
  const p90Pct = markerPct(parsed.p90Minutes);

  return (
    <div
      className="prediction-dist-bar"
      style={{ width: width || '100%', height }}
      aria-label="Landing time distribution"
    >
      <div className="prediction-dist-bar-bins">
        {bins.map(b => (
          <div
            key={b.idx}
            className="prediction-dist-bin"
            style={{
              flex: 1,
              background: `rgba(var(--prediction-dist-bin-rgb, 255, 107, 53), ${b.opacity})`,
            }}
            title={`${formatHM(b.startOffsetMin)}–${formatHM(b.endOffsetMin)}: ${(b.prob * 100).toFixed(1)}%`}
          />
        ))}
      </div>
      {p10Pct != null && (
        <span
          className="prediction-dist-marker prediction-dist-marker-edge"
          style={{ left: `${p10Pct}%` }}
          title={`p10 — ${formatHM(parsed.p10Minutes)}`}
        />
      )}
      {p50Pct != null && (
        <span
          className="prediction-dist-marker prediction-dist-marker-median"
          style={{ left: `${p50Pct}%` }}
          title={`p50 — ${formatHM(parsed.p50Minutes)} (median)`}
        />
      )}
      {p90Pct != null && (
        <span
          className="prediction-dist-marker prediction-dist-marker-edge"
          style={{ left: `${p90Pct}%` }}
          title={`p90 — ${formatHM(parsed.p90Minutes)}`}
        />
      )}
    </div>
  );
}
